import { OverlayReadinessStepId } from 'modules/overlay-v2/domain/interfaces/readiness';

/**
 * Все подписи чеклиста подготовки — в одном месте и без слов из мира разработки.
 * Экран видит человек с камерой и ноутбуком в спортзале: «журнал матча» вместо
 * `match_chronology`, «пульт» вместо realtime-канала, «логотипы и фото» вместо кэша.
 */

export const READINESS_TITLE = 'Подготовка трансляции';
export const READINESS_READY_TITLE = 'Всё готово';
export const READINESS_RETRY_BUTTON = 'Повторить';

/** Заголовки шагов — существительные: одинаково читаются в любом статусе. */
export const STEP_TITLES: Record<OverlayReadinessStepId, string> = {
  link: 'Ссылка на трансляцию',
  subscription: 'Доступ к трансляции',
  device: 'Связь с устройством',
  match: 'Запущенный матч',
  matchData: 'Данные матча',
  lineups: 'Составы команд',
  chronology: 'Журнал матча',
  matchTime: 'Время и период',
  template: 'Оформление табло',
  boards: 'Табло в оформлении',
  assets: 'Логотипы и фото игроков',
  remote: 'Связь с пультом',
};

/** Что делает шаг, пока крутится. */
export const STEP_RUNNING_DETAILS: Partial<Record<OverlayReadinessStepId, string>> = {
  subscription: 'Проверяем, оплачена ли трансляция',
  device: 'Ищем устройство из ссылки',
  match: 'Ищем матч на устройстве',
  matchData: 'Забираем счёт, фолы и составы',
  lineups: 'Смотрим, кто вышел на матч',
  chronology: 'Открываем журнал событий матча',
  matchTime: 'Сверяем секундомер и номер тайма',
  template: 'Загружаем оформление турнира',
  boards: 'Проверяем, есть ли что показывать',
  assets: 'Заранее скачиваем картинки',
  remote: 'Ждём отклик от приложения',
};

/** Подсказки «что сделать» — показываются только у жёлтых и красных шагов. */
export const STEP_HINTS = {
  subscriptionMissing: 'Трансляция входит в платную подписку — напишите в поддержку ImGame, её включат.',
  matchWaiting:
    'Запустите матч в мобильном приложении ImGame — мы подхватим его сами, перезагружать страницу не нужно. ' +
    'Если матч уже идёт, проверьте, что в приложении выбрано то же устройство, что и в этой ссылке.',
  noLineups: 'В приложении никого не отметили вышедшим на матч. Табло с составами показать не получится, остальные будут работать.',
  templateMissing: 'Откройте «Турниры» → нужный турнир → вкладка «Основное» → поле «Шаблон» и выберите оформление.',
  noBoards: 'Откройте «Шаблоны» → это оформление и добавьте табло — показывать пока нечего.',
  someImagesFailed: 'Часть картинок не открылась. Табло покажем, но на их местах будет пусто.',
  remoteTimedOut:
    'Пульт не отозвался. Данные будем запрашивать сами каждые 3 секунды — табло продолжит работать, ' +
    'но обновляться будет чуть медленнее.',
  networkRetry: 'Проверьте интернет — мы сами повторим попытку.',
} as const;

/** Ошибки, которые приходят от сервера, показываем человеку с понятной обёрткой. */
export const STEP_FAILURE_PREFIX: Partial<Record<OverlayReadinessStepId, string>> = {
  subscription: 'Не удалось проверить подписку',
  device: 'Не удалось связаться с устройством',
  template: 'Не удалось загрузить оформление',
  matchData: 'Не удалось загрузить данные матча',
  chronology: 'Не удалось открыть журнал матча',
  matchTime: 'Не удалось сверить время матча',
  assets: 'Не удалось скачать картинки',
};

/** Шаги, которые не держат показ табло. */
export const NON_BLOCKING_STEPS: OverlayReadinessStepId[] = ['remote'];

/**
 * Шаги, которые бессмысленно повторять. Ссылку чинят правкой адреса — это перезагружает
 * страницу целиком, поэтому кнопка «Повторить» и обратный отсчёт здесь только сбивают
 * с толку. Остальные шаги повторять стоит: подписку включает поддержка, оформление
 * выбирают в соседней вкладке, а сеть просто моргает — всё это подхватывается повтором.
 */
export const NON_RETRYABLE_STEPS: OverlayReadinessStepId[] = ['link'];

/* ---------- Тайминги ---------- */

/** Сколько ждём связь с пультом, прежде чем пропустить шаг с предупреждением. */
export const REMOTE_CONNECTION_TIMEOUT_MS = 15_000;

/**
 * Сколько чеклист держится на экране после того, как всё сошлось: успеваем показать
 * «Всё готово» и проиграть уход, а не подменить экран рывком посреди эфира.
 */
export const READINESS_EXIT_DURATION_MS = 700;
